import { ValueObject } from '../../../shared/models/ValueObject';

/**
 * 로그인 시도 값 객체
 */
export class LoginAttempt extends ValueObject<{
  email: string;
  timestamp: Date;
  success: boolean;
  ipAddress?: string;
  userAgent?: string;
}> {
  private readonly _email: string;
  private readonly _timestamp: Date;
  private readonly _success: boolean;
  private readonly _ipAddress?: string;
  private readonly _userAgent?: string;

  constructor(props: {
    email: string;
    timestamp?: Date;
    success: boolean;
    ipAddress?: string;
    userAgent?: string;
  }) {
    super();
    this.validate(props);
    this._email = props.email.toLowerCase().trim();
    this._timestamp = props.timestamp || new Date();
    this._success = props.success;
    this._ipAddress = props.ipAddress;
    this._userAgent = props.userAgent;
  }

  get email(): string {
    return this._email;
  }

  get timestamp(): Date {
    return new Date(this._timestamp);
  }

  get success(): boolean {
    return this._success;
  }

  get ipAddress(): string | undefined {
    return this._ipAddress;
  }

  get userAgent(): string | undefined {
    return this._userAgent;
  }

  private validate(props: {
    email: string;
    success: boolean;
  }): void {
    if (!props.email) {
      throw new Error('이메일은 비어있을 수 없습니다');
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(props.email)) {
      throw new Error('유효하지 않은 이메일 형식입니다');
    }

    if (typeof props.success !== 'boolean') {
      throw new Error('성공 여부는 boolean 값이어야 합니다');
    }
  }

  /**
   * 실패한 시도인지 확인
   */
  isFailed(): boolean {
    return !this._success;
  }

  /**
   * 최근 시도인지 확인 (기본: 5분 이내)
   */
  isRecent(minutes: number = 5): boolean {
    const now = new Date();
    const diff = now.getTime() - this._timestamp.getTime();
    return diff <= minutes * 60 * 1000;
  }

  /**
   * 시도 후 경과 시간 (분)
   */
  getMinutesAgo(): number {
    const now = new Date();
    const diff = now.getTime() - this._timestamp.getTime();
    return Math.floor(diff / (60 * 1000));
  }

  /**
   * 같은 IP에서의 시도인지 확인
   */
  isSameIP(other: LoginAttempt): boolean {
    return this._ipAddress === other._ipAddress && !!this._ipAddress;
  }

  /**
   * 브라우저 정보 추출
   */
  getBrowserXCircle(): {
    browser?: string;
    os?: string;
    device?: string;
  } {
    if (!this._userAgent) {
      return {};
    }

    const ua = this._userAgent.toLowerCase();
    let browser, os, device;

    // 브라우저 감지
    if (ua.includes('chrome')) browser = 'Chrome';
    else if (ua.includes('firefox')) browser = 'Firefox';
    else if (ua.includes('safari')) browser = 'Safari';
    else if (ua.includes('edge')) browser = 'Edge';

    // OS 감지
    if (ua.includes('windows')) os = 'Windows';
    else if (ua.includes('mac')) os = 'macOS';
    else if (ua.includes('linux')) os = 'Linux';
    else if (ua.includes('android')) os = 'Android';
    else if (ua.includes('ios')) os = 'iOS';

    // 디바이스 감지
    if (ua.includes('mobile')) device = 'Mobile';
    else if (ua.includes('tablet')) device = 'Tablet';
    else device = 'Desktop';

    return { browser, os, device };
  }

  /**
   * 위험도 점수 계산 (0-100)
   */
  getRiskScore(): number {
    let score = 0;

    // 실패한 시도
    if (!this._success) score += 30;

    // 최근 시도 (10분 이내)
    if (this.isRecent(10)) score += 20;

    // IP 주소 없음 (프록시 등)
    if (!this._ipAddress) score += 15;

    // User Agent 없음
    if (!this._userAgent) score += 10;

    // 야간 시간대 (22시-6시)
    const hour = this._timestamp.getHours();
    if (hour >= 22 || hour <= 6) score += 10;

    return Math.min(100, score);
  }

  protected equalsCore(vo: LoginAttempt): boolean {
    return (
      this._email === vo._email &&
      this._timestamp.getTime() === vo._timestamp.getTime() &&
      this._success === vo._success &&
      this._ipAddress === vo._ipAddress
    );
  }

  protected copy(): LoginAttempt {
    return new LoginAttempt({
      email: this._email,
      timestamp: this._timestamp,
      success: this._success,
      ipAddress: this._ipAddress,
      userAgent: this._userAgent,
    });
  }

  toString(): string {
    const status = this._success ? '성공' : '실패';
    const time = this._timestamp.toLocaleString('ko-KR');
    return `${this._email} - ${status} (${time})`;
  }

  /**
   * JSON 변환
   */
  toJSON(): object {
    return {
      email: this._email,
      timestamp: this._timestamp.toISOString(),
      success: this._success,
      ipAddress: this._ipAddress,
      browserXCircle: this.getBrowserXCircle(),
      minutesAgo: this.getMinutesAgo(),
      riskScore: this.getRiskScore(),
    };
  }

  /**
   * 팩토리 메서드
   */
  static success(email: string, options?: {
    ipAddress?: string;
    userAgent?: string;
  }): LoginAttempt {
    return new LoginAttempt({
      email,
      success: true,
      ...options,
    });
  }

  static failure(email: string, options?: {
    ipAddress?: string;
    userAgent?: string;
  }): LoginAttempt {
    return new LoginAttempt({
      email,
      success: false,
      ...options,
    });
  }
}